import { useState, Fragment } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Save, Pencil, X } from 'lucide-react';
import { api } from '../services/api';
import LoadingSpinner from '../components/Shared/LoadingSpinner';
import { fmtNumber } from '../components/Inventory/format';
import PageHeader from '../components/Shared/PageHeader';
import { btn, card, tbl } from '../theme';

function toEditForm(m) {
  return { cost_center_id: m.cost_center_id || '', notes: m.notes || '' };
}

// Fleetio yard names to the portal's cost centers, for R&M Dashboard access
// (migration 108). A cost center granted to someone reaches a yard only through a
// row here, so a change here changes who sees that yard.
export default function FleetYardMapping() {
  const qc = useQueryClient();
  const [unmappedOnly, setUnmappedOnly] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ cost_center_id: '', notes: '' });

  const { data: locations = [], isLoading } = useQuery({
    queryKey: ['fleet-yard-map'],
    queryFn:  () => api.get('/fleet-dashboard/yard-map'),
  });

  const { data: costCenters = [] } = useQuery({
    queryKey: ['admin-overtime-cost-centers'],
    queryFn:  () => api.get('/admin/overtime-cost-centers'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }) => api.patch(`/fleet-dashboard/yard-map/${id}`, payload),
    onSuccess: () => {
      toast.success('Mapping saved. It applies the next time each person opens the R&M Dashboard.');
      qc.invalidateQueries({ queryKey: ['fleet-yard-map'] });
      setEditingId(null);
    },
    onError: (err) => toast.error(err.message || 'Failed to save mapping.'),
  });

  function startEdit(m) {
    setEditingId(m.id);
    setEditForm(toEditForm(m));
  }

  function handleSave(id) {
    updateMutation.mutate({
      id,
      payload: { cost_center_id: editForm.cost_center_id || null, notes: editForm.notes.trim() || null },
    });
  }

  if (isLoading) return <LoadingSpinner />;

  const visible = unmappedOnly ? locations.filter((l) => !l.cost_center_id) : locations;
  const unmappedCount = locations.filter((l) => !l.cost_center_id).length;

  return (
    <div style={s.page}>
      <PageHeader
        eyebrow="Administration"
        title="Fleet Yard Mapping"
        subtitle="Map each Fleetio yard to a cost center. A person granted a cost center for the R&M Dashboard sees the yards mapped to it. Yards are added here when the dashboard first sees them, and a yard named exactly like one cost center is mapped to it."
      />

      <div style={s.actionsRow}>
        <label style={s.checkLabel}>
          <input type="checkbox" checked={unmappedOnly} onChange={(e) => setUnmappedOnly(e.target.checked)} />
          Unmapped only ({fmtNumber(unmappedCount)})
        </label>
        <span style={s.filterCount}>{visible.length} of {locations.length}</span>
      </div>

      <div style={s.card}>
        <div style={{ overflowX: 'auto' }}>
          {/* Wide table: it scrolls inside this, so the page does not. */}
        <table style={s.table}>
          <thead>
            <tr>
              {['Yard (as in Fleetio)', 'Cost Center', 'Notes', ''].map((h) => (
                <th key={h} style={s.th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visible.map((m) => {
              const isEditing = editingId === m.id;
              return (
                <Fragment key={m.id}>
                  <tr style={s.tr}>
                    <td style={s.td}><strong>{m.raw_yard_name}</strong></td>
                    <td style={s.td}>
                      {m.cost_center_id
                        ? `${m.cost_center_code} — ${m.cost_center_name}`
                        : <span style={s.unmapped}>Unmapped</span>}
                    </td>
                    <td style={{ ...s.td, color: '#9CA3AF' }}>{m.notes || '—'}</td>
                    <td style={{ ...s.td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button style={s.iconBtn} title="Edit mapping" onClick={() => (isEditing ? setEditingId(null) : startEdit(m))}>
                        {isEditing ? <X size={13} /> : <Pencil size={13} />}
                      </button>
                    </td>
                  </tr>
                  {isEditing && (
                    <tr style={s.editRow}>
                      <td colSpan={4} style={s.editTd}>
                        <div style={s.editGrid}>
                          <div style={s.field}>
                            <label style={s.label}>Cost Center</label>
                            <select style={s.input} value={editForm.cost_center_id} onChange={(e) => setEditForm({ ...editForm, cost_center_id: e.target.value })}>
                              <option value="">Unmapped</option>
                              {costCenters.map((cc) => (
                                <option key={cc.id} value={cc.id}>{cc.code} — {cc.name}</option>
                              ))}
                            </select>
                          </div>
                          <div style={{ ...s.field, gridColumn: '1 / -1' }}>
                            <label style={s.label}>Notes</label>
                            <input style={s.input} value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} placeholder="Optional" />
                          </div>
                        </div>
                        <div style={s.editActions}>
                          <button style={s.cancelBtn} onClick={() => setEditingId(null)}>Cancel</button>
                          <button style={s.saveBtn} onClick={() => handleSave(m.id)} disabled={updateMutation.isPending}>
                            <Save size={13} />
                            {updateMutation.isPending ? 'Saving…' : 'Save'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
            {!visible.length && (
              <tr><td colSpan={4} style={s.empty}>
                {locations.length ? 'Every yard is mapped.' : 'No yards seen yet. Open the R&M Dashboard once to list them.'}
              </td></tr>
            )}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
}

const s = {
  page:        { maxWidth: '1000px', margin: '0 auto' },
  actionsRow:  { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '10px' },
  checkLabel:  { display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#374151' },
  filterCount: { fontSize: '11px', color: '#9CA3AF' },
  card:        { ...card.base, overflow: 'hidden' },
  table:       { width: '100%', borderCollapse: 'collapse', fontSize: '12px' },
  th:          { ...tbl.th, ...tbl.th },
  tr:          { ...tbl.tr, ...tbl.tr },
  td:          { ...tbl.td, ...tbl.td },
  unmapped:    { fontSize: '11px', fontWeight: 600, color: '#A16207', background: '#FEF3C7', padding: '2px 8px', borderRadius: '10px' },
  iconBtn:     { ...btn.icon },
  editRow:     { borderBottom: '1px solid #F3F4F6', background: '#F9FAFB' },
  editTd:      { padding: '16px' },
  editGrid:    { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px', marginBottom: '12px' },
  field:       {},
  label:       { display: 'block', fontSize: '11px', fontWeight: 600, color: '#6B7280', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.5px' },
  input:       { width: '100%', boxSizing: 'border-box', padding: '8px 10px', border: '1px solid #D1D5DB', borderRadius: '6px', fontSize: '13px', color: '#374151', fontFamily: 'inherit', background: '#fff' },
  editActions: { display: 'flex', justifyContent: 'flex-end', gap: '8px' },
  saveBtn: {
    display: 'flex', alignItems: 'center', gap: '6px',
    padding: '8px 16px', fontSize: '12px', fontWeight: 600,
    border: 'none', borderRadius: '6px', background: '#1B2A4A',
    color: '#fff', cursor: 'pointer', fontFamily: 'inherit',
  },
  cancelBtn: {
    padding: '8px 16px', fontSize: '12px', border: '1px solid #D1D5DB',
    borderRadius: '6px', background: '#fff', color: '#6B7280', cursor: 'pointer', fontFamily: 'inherit',
  },
  empty:       { textAlign: 'center', padding: '32px', color: '#9CA3AF' },
};
